import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ColumnType } from '@myday/shared';

// ===== Types (local-state flavored) =====
export interface Workspace {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export interface Board {
  id: string;
  workspaceId: string;
  name: string;
  icon: string;
  color: string; // gradient class tail, e.g. 'from-neon-pink to-neon-purple'
  description?: string;
  starred?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Group {
  id: string;
  boardId: string;
  name: string;
  color: string;
  collapsed: boolean;
  position: number;
}

export interface Column {
  id: string;
  boardId: string;
  name: string;
  type: ColumnType;
  width: number;
  position: number;
  settings?: Record<string, unknown>;
}

export interface Item {
  id: string;
  boardId: string;
  groupId: string;
  name: string;
  position: number;
  values: Record<string, unknown>;
  parentItemId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface View {
  id: string;
  boardId: string;
  name: string;
  type: 'table' | 'kanban' | 'calendar' | 'timeline' | 'chart' | 'map' | 'files';
  isDefault: boolean;
  settings?: Record<string, unknown>;
}

// ===== Status + priority options =====
export const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started',  color: '#C4C4C4' },
  { value: 'working',     label: 'Working on it',color: '#FDAB3D' },
  { value: 'stuck',       label: 'Stuck',        color: '#E2445C' },
  { value: 'done',        label: 'Done',         color: '#00C875' },
];

export const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Low',      color: '#579BFC' },
  { value: 'medium',   label: 'Medium',   color: '#A25DDC' },
  { value: 'high',     label: 'High',     color: '#FDAB3D' },
  { value: 'critical', label: 'Critical', color: '#E2445C' },
];

export const PEOPLE = [
  { id: 'u_alona',   initials: 'AT', name: 'Alona',    color: 'from-neon-pink to-neon-purple' },
  { id: 'u_maya',    initials: 'MK', name: 'Maya',     color: 'from-neon-blue to-neon-cyan' },
  { id: 'u_noa',     initials: 'NS', name: 'Noa',      color: 'from-neon-yellow to-neon-orange' },
  { id: 'u_yoni',    initials: 'YL', name: 'Yoni',     color: 'from-neon-teal to-neon-lime' },
  { id: 'u_tal',     initials: 'TR', name: 'Tal',      color: 'from-neon-magenta to-neon-pink' },
];

export const GROUP_COLORS = ['#FDAB3D', '#00C875', '#579BFC', '#A25DDC', '#E2445C', '#2DD4BF', '#FACC15'];

// ===== ID helper =====
// Use native UUIDs when available so the same ids work for Supabase uuid columns.
// Falls back to a timestamp+random string for older browsers + the seed fixtures.
const uid = (prefix = 'id') => {
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID() as string;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
};

// ===== Seed data =====
function makeSeed() {
  const wsId = 'ws_main';

  type Seed = { board: Board; groups: Group[]; columns: Column[]; items: Item[]; views: View[] };

  function board(opts: { id: string; name: string; icon: string; color: string }): Board {
    return {
      id: opts.id, workspaceId: wsId, name: opts.name, icon: opts.icon, color: opts.color,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), starred: false,
    };
  }

  function seedBoard(b: Board, groupNames: string[], items: { group: number; name: string; status: string; priority?: string; person: string; date: string }[]): Seed {
    const groups: Group[] = groupNames.map((name, i) => ({
      id: uid('grp'), boardId: b.id, name, color: GROUP_COLORS[i % GROUP_COLORS.length], collapsed: false, position: i,
    }));

    const columns: Column[] = [
      { id: 'col_status',   boardId: b.id, name: 'Status',   type: 'status',   width: 140, position: 0 },
      { id: 'col_person',   boardId: b.id, name: 'Person',   type: 'person',   width: 110, position: 1 },
      { id: 'col_priority', boardId: b.id, name: 'Priority', type: 'priority', width: 130, position: 2 },
      { id: 'col_date',     boardId: b.id, name: 'Due date', type: 'date',     width: 140, position: 3 },
      { id: 'col_notes',    boardId: b.id, name: 'Notes',    type: 'text',     width: 240, position: 4 },
    ];

    const itemRows: Item[] = items.map((r, i) => ({
      id: uid('itm'),
      boardId: b.id,
      groupId: groups[r.group].id,
      name: r.name,
      position: i,
      values: {
        col_status:   r.status,
        col_person:   r.person,
        col_priority: r.priority ?? 'medium',
        col_date:     r.date,
        col_notes:    '',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    const views: View[] = [
      { id: uid('vw'), boardId: b.id, name: 'Main Table',  type: 'table',    isDefault: true  },
      { id: uid('vw'), boardId: b.id, name: 'Kanban',       type: 'kanban',   isDefault: false },
      { id: uid('vw'), boardId: b.id, name: 'Calendar',     type: 'calendar', isDefault: false },
      { id: uid('vw'), boardId: b.id, name: 'Timeline',     type: 'timeline', isDefault: false },
      { id: uid('vw'), boardId: b.id, name: 'Chart',        type: 'chart',    isDefault: false },
      { id: uid('vw'), boardId: b.id, name: 'Files',        type: 'files',    isDefault: false },
      { id: uid('vw'), boardId: b.id, name: 'Map',          type: 'map',      isDefault: false },
    ];

    return { board: b, groups, columns, items: itemRows, views };
  }

  const today = new Date();
  const iso = (offsetDays: number) => {
    const d = new Date(today);
    d.setDate(today.getDate() + offsetDays);
    return d.toISOString().slice(0, 10);
  };

  const s1 = seedBoard(
    board({ id: 'bd_marketing', name: 'Marketing Launch Q2', icon: '', color: 'from-neon-pink to-neon-purple' }),
    ['In progress', 'Done', 'Backlog'],
    [
      { group: 0, name: 'Draft landing page copy',    status: 'working',     priority: 'high',     person: 'u_alona', date: iso(3) },
      { group: 0, name: 'Design hero illustration',   status: 'working',     priority: 'medium',   person: 'u_maya',  date: iso(5) },
      { group: 0, name: 'Record product demo',        status: 'stuck',       priority: 'critical', person: 'u_noa',   date: iso(7) },
      { group: 0, name: 'Press kit + screenshots',    status: 'working',     priority: 'medium',   person: 'u_tal',   date: iso(6) },
      { group: 1, name: 'Kickoff meeting',            status: 'done',        priority: 'low',      person: 'u_alona', date: iso(-1) },
      { group: 1, name: 'Finalize brand palette',     status: 'done',        priority: 'medium',   person: 'u_maya',  date: iso(0) },
      { group: 2, name: 'Write press release',        status: 'not_started', priority: 'medium',   person: 'u_noa',   date: iso(11) },
      { group: 2, name: 'Partner outreach list',      status: 'not_started', priority: 'low',      person: 'u_alona', date: iso(13) },
      { group: 2, name: 'Influencer DM campaign',     status: 'not_started', priority: 'medium',   person: 'u_tal',   date: iso(14) },
    ],
  );

  const s2 = seedBoard(
    board({ id: 'bd_roadmap', name: 'Product Roadmap', icon: '', color: 'from-neon-blue to-neon-cyan' }),
    ['Now', 'Next', 'Later'],
    [
      { group: 0, name: 'Real-time cursors',         status: 'working', priority: 'high',     person: 'u_yoni', date: iso(4) },
      { group: 0, name: 'Automations v1',            status: 'working', priority: 'critical', person: 'u_alona', date: iso(10) },
      { group: 1, name: 'AI board generator',        status: 'not_started', priority: 'high', person: 'u_tal',  date: iso(21) },
      { group: 1, name: 'Gantt dependencies',        status: 'not_started', priority: 'medium',person: 'u_yoni', date: iso(24) },
      { group: 2, name: 'Mobile app',                status: 'not_started', priority: 'medium',person: 'u_noa',  date: iso(45) },
      { group: 2, name: 'Offline mode',              status: 'not_started', priority: 'low',   person: 'u_maya', date: iso(60) },
    ],
  );

  const s3 = seedBoard(
    board({ id: 'bd_sprint', name: 'Sprint Planning', icon: '', color: 'from-neon-yellow to-neon-orange' }),
    ['This sprint', 'Next sprint'],
    [
      { group: 0, name: 'Fix auth redirect bug',   status: 'working', priority: 'critical', person: 'u_yoni', date: iso(1) },
      { group: 0, name: 'Polish dashboard cards',  status: 'working', priority: 'medium',   person: 'u_maya', date: iso(2) },
      { group: 0, name: 'Add empty states',        status: 'done',    priority: 'low',      person: 'u_alona',date: iso(0) },
      { group: 1, name: 'Refactor column resizer', status: 'not_started', priority: 'low',  person: 'u_yoni', date: iso(9) },
      { group: 1, name: 'Dark mode QA pass',       status: 'not_started', priority: 'medium',person: 'u_tal', date: iso(10) },
    ],
  );

  const workspaces: Workspace[] = [
    { id: wsId, name: 'Main workspace', icon: '', color: '#A855F7' },
  ];

  const boards     = [s1.board,   s2.board,   s3.board];
  const groups     = [...s1.groups,  ...s2.groups,  ...s3.groups];
  const columns    = [...s1.columns, ...s2.columns, ...s3.columns];
  const items      = [...s1.items,   ...s2.items,   ...s3.items];
  const views      = [...s1.views,   ...s2.views,   ...s3.views];

  return { workspaces, boards, groups, columns, items, views };
}

// ===== Store =====
interface WorkspaceState {
  workspaces: Workspace[];
  boards: Board[];
  groups: Group[];
  columns: Column[];
  items: Item[];
  views: View[];
  activeWorkspaceId: string;

  // Workspace
  renameWorkspace: (workspaceId: string, name: string) => void;

  // Board CRUD
  createBoard: (input: { name: string; icon?: string; color?: string; workspaceId?: string }) => string;
  renameBoard: (boardId: string, name: string) => void;
  deleteBoard: (boardId: string) => void;
  toggleBoardStar: (boardId: string) => void;

  // Group CRUD
  createGroup: (boardId: string, name?: string) => string;
  renameGroup: (groupId: string, name: string) => void;
  setGroupColor: (groupId: string, color: string) => void;
  deleteGroup: (groupId: string) => void;
  toggleGroupCollapsed: (groupId: string) => void;

  // Item CRUD
  createItem: (boardId: string, groupId: string, name?: string, parentItemId?: string) => string;
  renameItem: (itemId: string, name: string) => void;
  updateItemValue: (itemId: string, columnId: string, value: unknown) => void;
  moveItem: (itemId: string, toGroupId: string) => void;
  deleteItem: (itemId: string) => void;

  // Column CRUD
  createColumn: (boardId: string, input: { name: string; type: ColumnType }) => string;
  renameColumn: (columnId: string, name: string) => void;
  updateColumnSettings: (columnId: string, settings: Record<string, unknown>) => void;
  deleteColumn: (columnId: string) => void;

  // Reset demo
  resetToSeed: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      ...makeSeed(),
      activeWorkspaceId: 'ws_main',

      // --- WORKSPACE ---
      renameWorkspace: (workspaceId, name) => set((s) => ({
        workspaces: s.workspaces.map((w) => w.id === workspaceId ? { ...w, name } : w),
      })),

      // --- BOARD ---
      createBoard: (input) => {
        const id = uid('bd');
        const now = new Date().toISOString();
        const board: Board = {
          id,
          workspaceId: input.workspaceId ?? get().activeWorkspaceId,
          name: input.name,
          icon: input.icon ?? '',
          color: input.color ?? 'from-neon-purple to-neon-blue',
          createdAt: now,
          updatedAt: now,
        };
        const defaultGroups: Group[] = [
          { id: uid('grp'), boardId: id, name: 'To do',  color: GROUP_COLORS[0], collapsed: false, position: 0 },
          { id: uid('grp'), boardId: id, name: 'Doing',  color: GROUP_COLORS[1], collapsed: false, position: 1 },
          { id: uid('grp'), boardId: id, name: 'Done',   color: GROUP_COLORS[2], collapsed: false, position: 2 },
        ];
        const defaultColumns: Column[] = [
          { id: uid('col'), boardId: id, name: 'Status',   type: 'status',   width: 140, position: 0 },
          { id: uid('col'), boardId: id, name: 'Person',   type: 'person',   width: 110, position: 1 },
          { id: uid('col'), boardId: id, name: 'Priority', type: 'priority', width: 130, position: 2 },
          { id: uid('col'), boardId: id, name: 'Due date', type: 'date',     width: 140, position: 3 },
        ];
        const defaultViews: View[] = [
          { id: uid('vw'), boardId: id, name: 'Main Table', type: 'table',    isDefault: true  },
          { id: uid('vw'), boardId: id, name: 'Kanban',     type: 'kanban',   isDefault: false },
          { id: uid('vw'), boardId: id, name: 'Calendar',   type: 'calendar', isDefault: false },
          { id: uid('vw'), boardId: id, name: 'Timeline',   type: 'timeline', isDefault: false },
          { id: uid('vw'), boardId: id, name: 'Chart',      type: 'chart',    isDefault: false },
          { id: uid('vw'), boardId: id, name: 'Files',      type: 'files',    isDefault: false },
          { id: uid('vw'), boardId: id, name: 'Map',        type: 'map',      isDefault: false },
        ];
        set((s) => ({
          boards: [...s.boards, board],
          groups: [...s.groups, ...defaultGroups],
          columns: [...s.columns, ...defaultColumns],
          views: [...s.views, ...defaultViews],
        }));
        return id;
      },
      renameBoard: (boardId, name) => set((s) => ({
        boards: s.boards.map((b) => b.id === boardId ? { ...b, name, updatedAt: new Date().toISOString() } : b),
      })),
      deleteBoard: (boardId) => set((s) => ({
        boards:  s.boards.filter((b) => b.id !== boardId),
        groups:  s.groups.filter((g) => g.boardId !== boardId),
        columns: s.columns.filter((c) => c.boardId !== boardId),
        items:   s.items.filter((i) => i.boardId !== boardId),
        views:   s.views.filter((v) => v.boardId !== boardId),
      })),
      toggleBoardStar: (boardId) => set((s) => ({
        boards: s.boards.map((b) => b.id === boardId ? { ...b, starred: !b.starred } : b),
      })),

      // --- GROUP ---
      createGroup: (boardId, name = 'New group') => {
        const id = uid('grp');
        set((s) => {
          const boardGroups = s.groups.filter((g) => g.boardId === boardId);
          const color = GROUP_COLORS[boardGroups.length % GROUP_COLORS.length];
          return {
            groups: [...s.groups, { id, boardId, name, color, collapsed: false, position: boardGroups.length }],
          };
        });
        return id;
      },
      renameGroup: (groupId, name) => set((s) => ({
        groups: s.groups.map((g) => g.id === groupId ? { ...g, name } : g),
      })),
      setGroupColor: (groupId, color) => set((s) => ({
        groups: s.groups.map((g) => g.id === groupId ? { ...g, color } : g),
      })),
      deleteGroup: (groupId) => set((s) => ({
        groups: s.groups.filter((g) => g.id !== groupId),
        items:  s.items.filter((i) => i.groupId !== groupId),
      })),
      toggleGroupCollapsed: (groupId) => set((s) => ({
        groups: s.groups.map((g) => g.id === groupId ? { ...g, collapsed: !g.collapsed } : g),
      })),

      // --- ITEM ---
      createItem: (boardId, groupId, name = 'New item', parentItemId) => {
        const id = uid('itm');
        const now = new Date().toISOString();
        set((s) => {
          const inGroup = s.items.filter((i) => i.groupId === groupId && i.parentItemId === parentItemId);
          return {
            items: [...s.items, {
              id, boardId, groupId, name,
              position: inGroup.length,
              values: {},
              parentItemId,
              createdAt: now, updatedAt: now,
            }],
          };
        });
        return id;
      },
      renameItem: (itemId, name) => set((s) => ({
        items: s.items.map((i) => i.id === itemId ? { ...i, name, updatedAt: new Date().toISOString() } : i),
      })),
      updateItemValue: (itemId, columnId, value) => set((s) => ({
        items: s.items.map((i) =>
          i.id === itemId
            ? { ...i, values: { ...i.values, [columnId]: value }, updatedAt: new Date().toISOString() }
            : i,
        ),
      })),
      moveItem: (itemId, toGroupId) => set((s) => ({
        items: s.items.map((i) => i.id === itemId ? { ...i, groupId: toGroupId, updatedAt: new Date().toISOString() } : i),
      })),
      deleteItem: (itemId) => set((s) => ({
        items: s.items.filter((i) => i.id !== itemId),
      })),

      // --- COLUMN ---
      createColumn: (boardId, input) => {
        const id = uid('col');
        set((s) => {
          const boardCols = s.columns.filter((c) => c.boardId === boardId);
          return {
            columns: [...s.columns, {
              id, boardId,
              name: input.name,
              type: input.type,
              width: 160,
              position: boardCols.length,
            }],
          };
        });
        return id;
      },
      renameColumn: (columnId, name) => set((s) => ({
        columns: s.columns.map((c) => c.id === columnId ? { ...c, name } : c),
      })),
      updateColumnSettings: (columnId, settings) => set((s) => ({
        columns: s.columns.map((c) => c.id === columnId
          ? { ...c, settings: { ...(c.settings ?? {}), ...settings } }
          : c,
        ),
      })),
      deleteColumn: (columnId) => set((s) => ({
        columns: s.columns.filter((c) => c.id !== columnId),
        items: s.items.map((i) => {
          if (i.values[columnId] === undefined) return i;
          const next = { ...i.values };
          delete next[columnId];
          return { ...i, values: next };
        }),
      })),

      resetToSeed: () => set({ ...makeSeed(), activeWorkspaceId: 'ws_main' }),
    }),
    {
      name: 'myday-workspace',
      version: 2,
      // v2 strips decorative icons from all persisted boards + workspaces so
      // existing users don't keep seeing legacy emoji after the design rewrite.
      migrate: (persisted: any, fromVersion) => {
        if (!persisted) return persisted;
        if (fromVersion < 2) {
          if (Array.isArray(persisted.boards)) {
            persisted.boards = persisted.boards.map((b: any) => ({ ...b, icon: '' }));
          }
          if (Array.isArray(persisted.workspaces)) {
            persisted.workspaces = persisted.workspaces.map((w: any) => ({ ...w, icon: '' }));
          }
        }
        return persisted;
      },
    },
  ),
);

// ===== Selectors =====
// These return new filtered/sorted arrays per call. Without memoization,
// Zustand's default equality check would see a new ref every render, forcing
// components into infinite re-renders (React error #185). Cache by source.
function memoSelector<T>(compute: (s: WorkspaceState) => T): (s: WorkspaceState) => T {
  let cache: { s: WorkspaceState; out: T } | null = null;
  return (s) => {
    if (cache && cache.s === s) return cache.out;
    const out = compute(s);
    cache = { s, out };
    return out;
  };
}

const _boardById      = new Map<string, (s: WorkspaceState) => Board | undefined>();
const _groupsByBoard  = new Map<string, (s: WorkspaceState) => Group[]>();
const _columnsByBoard = new Map<string, (s: WorkspaceState) => Column[]>();
const _itemsByBoard   = new Map<string, (s: WorkspaceState) => Item[]>();
const _viewsByBoard   = new Map<string, (s: WorkspaceState) => View[]>();

export const selectBoard = (boardId: string) => {
  let fn = _boardById.get(boardId);
  if (!fn) { fn = memoSelector((s) => s.boards.find((b) => b.id === boardId)); _boardById.set(boardId, fn); }
  return fn;
};

export const selectGroupsForBoard = (boardId: string) => {
  let fn = _groupsByBoard.get(boardId);
  if (!fn) {
    fn = memoSelector((s) => s.groups.filter((g) => g.boardId === boardId).sort((a, b) => a.position - b.position));
    _groupsByBoard.set(boardId, fn);
  }
  return fn;
};

export const selectColumnsForBoard = (boardId: string) => {
  let fn = _columnsByBoard.get(boardId);
  if (!fn) {
    fn = memoSelector((s) => s.columns.filter((c) => c.boardId === boardId).sort((a, b) => a.position - b.position));
    _columnsByBoard.set(boardId, fn);
  }
  return fn;
};

export const selectItemsForBoard = (boardId: string) => {
  let fn = _itemsByBoard.get(boardId);
  if (!fn) { fn = memoSelector((s) => s.items.filter((i) => i.boardId === boardId)); _itemsByBoard.set(boardId, fn); }
  return fn;
};

export const selectViewsForBoard = (boardId: string) => {
  let fn = _viewsByBoard.get(boardId);
  if (!fn) { fn = memoSelector((s) => s.views.filter((v) => v.boardId === boardId)); _viewsByBoard.set(boardId, fn); }
  return fn;
};
